import { useSyncExternalStore } from 'react';

/**
 * App-wide transient UI: toasts and confirm dialogs. Module stores rather than context so
 * any function — mutation callbacks, plain helpers — can raise one without a hook or prop
 * drilling. Hosts (<ToastHost/>, <ConfirmHost/>) render them from App.
 */

// --- Toasts -----------------------------------------------------------------

export type ToastTone = 'info' | 'success' | 'error';
export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

let toasts: Toast[] = [];
let nextId = 1;
const toastListeners = new Set<() => void>();

function emitToasts(): void {
  toasts = [...toasts];
  for (const l of toastListeners) l();
}

export function toast(message: string, tone: ToastTone = 'info'): void {
  const id = nextId++;
  toasts = [...toasts, { id, message, tone }];
  emitToasts();
  // Errors linger a little longer — they carry more to read.
  const ttl = tone === 'error' ? 6000 : 3500;
  setTimeout(() => dismissToast(id), ttl);
}

export function dismissToast(id: number): void {
  toasts = toasts.filter((t) => t.id !== id);
  emitToasts();
}

export function useToasts(): Toast[] {
  return useSyncExternalStore(
    (l) => {
      toastListeners.add(l);
      return () => toastListeners.delete(l);
    },
    () => toasts,
    () => toasts,
  );
}

// --- Confirm ----------------------------------------------------------------

export interface ConfirmRequest {
  title: string;
  body?: string;
  confirmLabel: string;
  danger: boolean;
  resolve: (ok: boolean) => void;
}

let current: ConfirmRequest | null = null;
const confirmListeners = new Set<() => void>();

function emitConfirm(): void {
  for (const l of confirmListeners) l();
}

/** Awaitable confirm: `if (await confirmAction({...})) doThing()`. */
export function confirmAction(opts: {
  title: string;
  body?: string;
  confirmLabel?: string;
  danger?: boolean;
}): Promise<boolean> {
  // A second request supersedes the first (the pending one resolves false).
  current?.resolve(false);
  return new Promise<boolean>((resolve) => {
    current = {
      title: opts.title,
      body: opts.body,
      confirmLabel: opts.confirmLabel ?? 'Confirm',
      danger: opts.danger ?? false,
      resolve,
    };
    emitConfirm();
  });
}

/** Convenience for the common "really delete X?" case. */
export function confirmDelete(what: string, body?: string): Promise<boolean> {
  return confirmAction({ title: `Delete ${what}?`, body, confirmLabel: 'Delete', danger: true });
}

export function resolveConfirm(ok: boolean): void {
  current?.resolve(ok);
  current = null;
  emitConfirm();
}

export function useConfirm(): ConfirmRequest | null {
  return useSyncExternalStore(
    (l) => {
      confirmListeners.add(l);
      return () => confirmListeners.delete(l);
    },
    () => current,
    () => current,
  );
}
